import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap, TrendingUp, UserX, AlertTriangle, Users2, ShieldAlert, CalendarClock, Award, History, UserPlus,
} from "lucide-react";
import { api } from "@/lib/api";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { StatCard, PageHeader, Loading, EmptyState } from "@/components/shared";

export default function Dashboard() {
  const { fiscalYear } = useFiscalYear();
  const [data, setData] = useState(null);
  const [onb, setOnb] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!fiscalYear) return;
    setLoading(true);
    api.get(`/dashboard?fiscal_year=${fiscalYear}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
    api.get(`/onboarding/dashboard?fiscal_year=${fiscalYear}`).then(({ data }) => setOnb(data)).catch(() => {});
  }, [fiscalYear]);

  if (loading || !data) return <Loading />;
  const k = data.kpi || {};

  return (
    <div>
      <PageHeader title="ホーム" subtitle={`${fiscalYear}年度の実施状況ダッシュボード`} />

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <StatCard testid="kpi-training-rate" label="本年度研修実施率" value={k.training_rate ?? 0} unit="%" icon={TrendingUp} tone="blue" />
        <StatCard testid="kpi-completion-rate" label="職員修了率" value={k.completion_rate ?? 0} unit="%" icon={GraduationCap} tone="green" />
        <StatCard testid="kpi-not-started" label="未受講人数" value={k.not_started ?? 0} unit="名" icon={UserX} tone="amber" />
        <StatCard testid="kpi-overdue" label="期限超過人数" value={k.overdue ?? 0} unit="名" icon={AlertTriangle} tone="rose" />
        <StatCard testid="kpi-committee" label="未実施委員会" value={k.committee_pending ?? 0} unit="件" icon={Users2} tone="slate" />
        <StatCard testid="kpi-drill" label="未実施訓練" value={k.drill_pending ?? 0} unit="件" icon={ShieldAlert} tone="slate" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 lg:col-span-3 cursor-pointer hover:border-blue-200 transition-colors" onClick={() => navigate("/onboarding")} data-testid="dashboard-onboarding-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><UserPlus className="w-4 h-4 text-blue-600" /><h3 className="font-semibold text-slate-800">新規職員 入社研修</h3></div>
            <span className="text-xs text-blue-600">一覧を開く →</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {[["今年度入職", onb?.this_year_hires, "text-slate-800"], ["対象者", onb?.targets, "text-slate-800"],
              ["未開始", onb?.not_started, "text-slate-500"], ["受講中", onb?.in_progress, "text-blue-600"],
              ["管理者確認待ち", onb?.pending_confirm, "text-amber-600"], ["修了", onb?.completed, "text-green-600"]].map(([l, v, c]) => (
              <div key={l} className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-center">
                <div className={`text-2xl font-bold ${c}`}>{v ?? 0}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{l}</div>
              </div>
            ))}
          </div>
          {(onb?.overdue ?? 0) > 0 && <div className="mt-3 text-xs text-rose-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />期限超過 {onb.overdue}名 — フォローが必要です</div>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4"><CalendarClock className="w-4 h-4 text-blue-600" /><h3 className="font-semibold text-slate-800">今月の予定</h3></div>
          {data.upcoming?.length ? (
            <ul className="space-y-2">
              {data.upcoming.map((u, i) => (
                <li key={i} className="flex items-center justify-between text-sm py-2 border-b border-slate-50 last:border-0">
                  <span className="text-slate-700">{u.name}</span>
                  <span className="text-xs text-slate-400">{u.type}</span>
                </li>
              ))}
            </ul>
          ) : <EmptyState title="今月の予定はありません" />}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4"><Award className="w-4 h-4 text-green-600" /><h3 className="font-semibold text-slate-800">最近の修了者</h3></div>
          {data.recent_certificates?.length ? (
            <ul className="space-y-2">
              {data.recent_certificates.map((c) => (
                <li key={c.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-50 last:border-0 cursor-pointer hover:text-blue-600"
                    onClick={() => navigate("/certificates")}>
                  <span className="text-slate-700">{c.user_name}</span>
                  <span className="text-xs text-slate-400 truncate ml-2">{c.training_name}</span>
                </li>
              ))}
            </ul>
          ) : <EmptyState title="修了者はまだいません" />}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4"><History className="w-4 h-4 text-slate-600" /><h3 className="font-semibold text-slate-800">最近の操作</h3></div>
          {data.recent_logs?.length ? (
            <ul className="space-y-2">
              {data.recent_logs.map((l) => (
                <li key={l.id} className="text-sm py-2 border-b border-slate-50 last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">{l.action}</span>
                    <span className="text-xs text-slate-400">{(l.created_at || "").slice(5, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-xs text-slate-400">{l.user_name}</div>
                </li>
              ))}
            </ul>
          ) : <EmptyState title="操作履歴はありません" />}
        </div>
      </div>
    </div>
  );
}
